import React from 'react';
import { useLocation } from 'react-router-dom';

import {
  CANONICAL_SCROLL_CONTINUATION_CANCEL_EVENT,
  CANONICAL_SCROLL_CONTINUATION_EVENT,
  isCanonicalScrollContinuationRequest,
  type CanonicalScrollContinuationRequest,
} from './canonicalReplace';
import { useNavigationHistory } from './NavigationHistoryProvider';
import {
  createScrollRegistry,
  scrollIdentity,
  type ScrollIdentity,
  type ScrollRegistry,
} from './scrollRegistry';
import { NAVIGATION_PURGE_EVENT } from './sessionStore';

interface ReadyReport {
  scopeId: string;
  locationKey: string;
  ready: boolean;
  viewKey: string;
}

interface ActiveTransition {
  scopeId: string;
  locationKey: string;
  action: 'POP' | 'PUSH' | 'REPLACE';
}

interface NavigationScrollContextValue {
  isRestoring(node: HTMLElement): boolean;
  effectiveNavigationAction: 'POP' | 'PUSH' | 'REPLACE';
  reportReady(owner: symbol, report: ReadyReport): () => void;
  bindNamed(owner: symbol, binding: NamedBindingRequest): () => void;
  reportNamedReady(owner: symbol, report: NamedReadyReport): () => void;
}

interface NamedBindingRequest {
  scopeId: string;
  locationKey: string;
  viewKey: string;
  key: string;
  node: HTMLElement;
  navigationAction: 'POP' | 'PUSH' | 'REPLACE';
}

interface NamedReadyReport {
  locationKey: string;
  action: 'POP' | 'PUSH' | 'REPLACE';
  ready: boolean;
}

interface ActiveNamedBinding extends NamedBindingRequest {
  identity: ScrollIdentity;
  encodedIdentity: string;
  action: 'POP' | 'PUSH' | 'REPLACE';
  ready: boolean;
  active: boolean;
  generation: number;
  restoreGuard: boolean;
  protectSavedSnapshot: boolean;
  cancelRestore: (() => void) | null;
}

const NavigationScrollContext = React.createContext<NavigationScrollContextValue | null>(null);
export const NAVIGATION_SCROLL_RESTORED = 'navigation:scroll-restored';
const noPendingRestoration = () => false;

/** Read-only observation for presentation; does not save, restore or register a scroller. */
// eslint-disable-next-line react-refresh/only-export-components
export function useNavigationScrollRestoration() {
  return React.useContext(NavigationScrollContext)?.isRestoring ?? noPendingRestoration;
}

function unavailableStorage(): Storage {
  const unavailable = () => { throw new Error('sessionStorage is unavailable'); };
  return {
    get length(): number { return unavailable(); },
    clear: unavailable,
    getItem: unavailable,
    key: unavailable,
    removeItem: unavailable,
    setItem: unavailable,
  };
}

function defaultRegistry(): ScrollRegistry {
  let storage: Storage;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = unavailableStorage();
  }
  return createScrollRegistry(storage, () => Date.now());
}

function afterTwoFrames(
  callback: () => void,
): () => void {
  let cancelled = false;
  let firstFrame: number | null = null;
  let secondFrame: number | null = null;
  firstFrame = window.requestAnimationFrame(() => {
    if (cancelled) return;
    secondFrame = window.requestAnimationFrame(() => {
      if (!cancelled) callback();
    });
  });
  return () => {
    cancelled = true;
    if (firstFrame !== null) window.cancelAnimationFrame(firstFrame);
    if (secondFrame !== null) window.cancelAnimationFrame(secondFrame);
  };
}

function encodedScrollIdentity(identity: ScrollIdentity): string {
  return scrollIdentity(
    identity.scopeId,
    identity.locationKey,
    identity.viewKey,
    identity.target,
  );
}

function useRequiredScrollContext(): NavigationScrollContextValue {
  const value = React.useContext(NavigationScrollContext);
  if (!value) {
    throw new Error('Navigation scroll hooks must be used within NavigationScrollProvider');
  }
  return value;
}

export function NavigationScrollProvider({
  children,
  registry: suppliedRegistry,
}: {
  children: React.ReactNode;
  registry?: ScrollRegistry;
}) {
  const history = useNavigationHistory();
  const routerLocation = useLocation();
  const registryRef = React.useRef<ScrollRegistry | null>(null);
  if (registryRef.current === null) registryRef.current = suppliedRegistry ?? defaultRegistry();
  const registry = registryRef.current;
  const transitionRef = React.useRef<ActiveTransition | null>(null);
  const activeWindowRef = React.useRef<ScrollIdentity | null>(null);
  const readyReportsRef = React.useRef(new Map<symbol, ReadyReport>());
  const activeReadyOwnerRef = React.useRef<symbol | null>(null);
  const namedBindingsRef = React.useRef(new Map<symbol, ActiveNamedBinding>());
  const previousNamedIdentityRef = React.useRef(new Map<symbol, ScrollIdentity>());
  const cancelRestoreRef = React.useRef<(() => void) | null>(null);
  const windowRestoreGuardRef = React.useRef<string | null>(null);
  const activationGenerationRef = React.useRef(0);
  const purgedRef = React.useRef(false);
  const canonicalContinuationRef = React.useRef<CanonicalScrollContinuationRequest | null>(null);
  const previousTransition = transitionRef.current;
  const requestedContinuation = canonicalContinuationRef.current;
  const continuesRequestedPop = requestedContinuation?.scopeId === history.scopeId
    && requestedContinuation.fromLocationKey === previousTransition?.locationKey
    && requestedContinuation.pathname === routerLocation.pathname
    && requestedContinuation.search === routerLocation.search
    && requestedContinuation.hash === routerLocation.hash;
  const effectiveNavigationAction = history.action === 'REPLACE'
    && previousTransition?.action === 'POP'
    && previousTransition.scopeId === history.scopeId
    && (previousTransition.locationKey === history.location.key
      || continuesRequestedPop)
    ? 'POP'
    : history.action;

  const cancelRestore = React.useCallback(() => {
    cancelRestoreRef.current?.();
    cancelRestoreRef.current = null;
  }, []);

  const saveActiveWindow = React.useCallback(() => {
    const active = activeWindowRef.current;
    if (active
      && !purgedRef.current
      && windowRestoreGuardRef.current !== encodedScrollIdentity(active)) {
      registry.write(active, { x: window.scrollX, y: window.scrollY });
    }
  }, [registry]);

  const cancelNamedRestore = React.useCallback((binding: ActiveNamedBinding) => {
    binding.cancelRestore?.();
    binding.cancelRestore = null;
    // A navigation can supersede a scheduled two-frame restore before its
    // callback clears the guard. The outgoing binding must become saveable
    // immediately, otherwise its real scroll position is silently discarded.
    if (!binding.protectSavedSnapshot) binding.restoreGuard = false;
  }, []);

  const saveNamedBinding = React.useCallback((binding: ActiveNamedBinding) => {
    if (!binding.active
      || binding.restoreGuard
      || binding.generation !== activationGenerationRef.current
      || purgedRef.current) return;
    registry.write(binding.identity, {
      x: binding.node.scrollLeft,
      y: binding.node.scrollTop,
    });
  }, [registry]);

  const scheduleNamedRestore = React.useCallback((binding: ActiveNamedBinding) => {
    cancelNamedRestore(binding);
    if (!binding.active || binding.action !== 'POP' || purgedRef.current) return;
    const saved = registry.read(binding.identity);
    if (!saved) return;
    binding.restoreGuard = true;
    if (!binding.ready) {
      binding.protectSavedSnapshot = true;
      return;
    }
    binding.protectSavedSnapshot = false;
    const generation = binding.generation;
    binding.cancelRestore = afterTwoFrames(() => {
      if (!binding.active
        || purgedRef.current
        || activationGenerationRef.current !== generation
        || binding.generation !== generation) return;
      binding.node.scrollLeft = saved.x;
      binding.node.scrollTop = saved.y;
      binding.restoreGuard = false;
      binding.protectSavedSnapshot = false;
      binding.cancelRestore = null;
      binding.node.dispatchEvent(new Event(NAVIGATION_SCROLL_RESTORED));
    });
  }, [cancelNamedRestore, registry]);

  const activateNamedBinding = React.useCallback((binding: ActiveNamedBinding) => {
    if (purgedRef.current) return;
    if (import.meta.env.DEV) {
      for (const candidate of namedBindingsRef.current.values()) {
        if (candidate !== binding
          && candidate.active
          && candidate.encodedIdentity === binding.encodedIdentity
          && candidate.node !== binding.node) {
          throw new Error(`Duplicate navigation scroll identity: ${binding.encodedIdentity}`);
        }
      }
    }
    binding.active = true;
    binding.generation = activationGenerationRef.current;
    binding.node.setAttribute('data-navigation-scroll-key', binding.key);
    scheduleNamedRestore(binding);
  }, [scheduleNamedRestore]);

  const activateCurrentNamedBindings = React.useCallback(() => {
    const transition = transitionRef.current;
    if (!transition || purgedRef.current) return;
    for (const binding of namedBindingsRef.current.values()) {
      if (binding.scopeId === transition.scopeId
        && binding.locationKey === transition.locationKey) {
        activateNamedBinding(binding);
      }
    }
  }, [activateNamedBinding]);

  const activateCurrentReport = React.useCallback(() => {
    if (purgedRef.current) return;
    const transition = transitionRef.current;
    if (!transition) return;
    const reportEntry = [...readyReportsRef.current.entries()]
      .find(([, candidate]) => candidate.locationKey === transition.locationKey);
    if (!reportEntry) return;
    const [owner, report] = reportEntry;
    activeReadyOwnerRef.current = owner;
    const identity: ScrollIdentity = {
      scopeId: transition.scopeId,
      locationKey: transition.locationKey,
      viewKey: report.viewKey,
      target: 'window',
    };
    const active = activeWindowRef.current;
    const activeIdentityKey = active ? encodedScrollIdentity(active) : null;
    const identityKey = encodedScrollIdentity(identity);
    const identityChanged = activeIdentityKey !== null && activeIdentityKey !== identityKey;
    if (identityChanged) {
      saveActiveWindow();
    }
    activeWindowRef.current = identity;
    cancelRestore();
    if (identityChanged && windowRestoreGuardRef.current === activeIdentityKey) {
      windowRestoreGuardRef.current = null;
    }
    if (transition.action !== 'POP') return;
    const saved = registry.read(identity);
    if (!saved) return;
    windowRestoreGuardRef.current = identityKey;
    if (!report.ready) return;
    const generation = activationGenerationRef.current;
    cancelRestoreRef.current = afterTwoFrames(() => {
      if (activationGenerationRef.current !== generation
        || transitionRef.current !== transition) return;
      window.scrollTo({ left: saved.x, top: saved.y, behavior: 'auto' });
      (document.scrollingElement ?? document.documentElement).dispatchEvent(new Event(NAVIGATION_SCROLL_RESTORED));
      if (windowRestoreGuardRef.current === identityKey) {
        windowRestoreGuardRef.current = null;
      }
      cancelRestoreRef.current = null;
    });
  }, [cancelRestore, registry, saveActiveWindow]);

  const reportReady = React.useCallback((owner: symbol, report: ReadyReport) => {
    scrollIdentity(report.scopeId, report.locationKey, report.viewKey, 'window');
    if (purgedRef.current) return () => undefined;
    if (import.meta.env.DEV) {
      for (const [candidateOwner, candidate] of readyReportsRef.current) {
        if (candidateOwner !== owner && candidate.locationKey === report.locationKey) {
          throw new Error(`Duplicate navigation scroll readiness owner: ${report.locationKey}`);
        }
      }
    }
    readyReportsRef.current.set(owner, report);
    if (transitionRef.current?.locationKey === report.locationKey) {
      activateCurrentReport();
    }
    return () => {
      const current = readyReportsRef.current.get(owner);
      if (current === report) {
        readyReportsRef.current.delete(owner);
        if (activeReadyOwnerRef.current === owner) {
          activeReadyOwnerRef.current = null;
          cancelRestore();
        }
      }
    };
  }, [activateCurrentReport, cancelRestore]);

  const bindNamed = React.useCallback((owner: symbol, request: NamedBindingRequest) => {
    const identity: ScrollIdentity = {
      scopeId: request.scopeId,
      locationKey: request.locationKey,
      viewKey: request.viewKey,
      target: `named:${request.key}`,
    };
    const binding: ActiveNamedBinding = {
      ...request,
      identity,
      encodedIdentity: scrollIdentity(
        identity.scopeId,
        identity.locationKey,
        identity.viewKey,
        identity.target,
      ),
      action: 'POP',
      ready: false,
      active: false,
      generation: activationGenerationRef.current,
      restoreGuard: false,
      protectSavedSnapshot: false,
      cancelRestore: null,
    };
    const previousIdentity = previousNamedIdentityRef.current.get(owner);
    previousNamedIdentityRef.current.delete(owner);
    if (!purgedRef.current
      && request.navigationAction === 'REPLACE'
      && previousIdentity
      && previousIdentity.scopeId === identity.scopeId
      && previousIdentity.locationKey !== identity.locationKey
      && previousIdentity.viewKey === identity.viewKey
      && previousIdentity.target === identity.target) {
      registry.remove(previousIdentity, identity);
    }
    if (purgedRef.current) return () => undefined;
    namedBindingsRef.current.set(owner, binding);
    const transition = transitionRef.current;
    if (transition?.scopeId === binding.scopeId
      && transition.locationKey === binding.locationKey) {
      activateNamedBinding(binding);
    }
    return () => {
      cancelNamedRestore(binding);
      saveNamedBinding(binding);
      if (!purgedRef.current) previousNamedIdentityRef.current.set(owner, binding.identity);
      if (namedBindingsRef.current.get(owner) === binding) namedBindingsRef.current.delete(owner);
      if (binding.node.getAttribute('data-navigation-scroll-key') === binding.key) {
        binding.node.removeAttribute('data-navigation-scroll-key');
      }
      binding.active = false;
    };
  }, [activateNamedBinding, cancelNamedRestore, registry, saveNamedBinding]);

  const reportNamedReady = React.useCallback((owner: symbol, report: NamedReadyReport) => {
    const binding = namedBindingsRef.current.get(owner);
    if (!binding || binding.locationKey !== report.locationKey) return () => undefined;
    binding.action = report.action;
    binding.ready = report.ready;
    const transition = transitionRef.current;
    if (transition?.scopeId === binding.scopeId
      && transition.locationKey === binding.locationKey) {
      if (!binding.active) activateNamedBinding(binding);
      else scheduleNamedRestore(binding);
    }
    return () => {
      if (binding.action === report.action && binding.ready === report.ready) {
        binding.ready = false;
        cancelNamedRestore(binding);
      }
    };
  }, [activateNamedBinding, cancelNamedRestore, scheduleNamedRestore]);

  React.useLayoutEffect(() => {
    const previous = transitionRef.current;
    const next: ActiveTransition = {
      scopeId: history.scopeId,
      locationKey: history.location.key,
      action: effectiveNavigationAction,
    };
    if (previous?.scopeId === next.scopeId && previous.locationKey === next.locationKey) {
      transitionRef.current = next;
      activateCurrentReport();
      return;
    }

    if (purgedRef.current) {
      transitionRef.current = next;
      return;
    }

    cancelRestore();
    for (const binding of namedBindingsRef.current.values()) {
      cancelNamedRestore(binding);
      saveNamedBinding(binding);
      binding.active = false;
    }
    activationGenerationRef.current += 1;
    const outgoing = activeWindowRef.current;
    if (outgoing && previous && outgoing.locationKey === previous.locationKey) {
      saveActiveWindow();
    }

    transitionRef.current = next;
    if (requestedContinuation?.fromLocationKey === previous?.locationKey) {
      canonicalContinuationRef.current = null;
    }
    activeWindowRef.current = null;
    if (history.action === 'REPLACE' && outgoing && previous) {
      const replacement = { ...outgoing, locationKey: next.locationKey };
      registry.remove(outgoing, replacement);
      activeWindowRef.current = replacement;
    } else if (history.action === 'PUSH' && previous) {
      window.scrollTo({ left: 0, top: 0, behavior: 'auto' });
    }
    windowRestoreGuardRef.current = null;
    activateCurrentReport();
    activateCurrentNamedBindings();
  }, [
    activateCurrentNamedBindings,
    activateCurrentReport,
    cancelNamedRestore,
    cancelRestore,
    continuesRequestedPop,
    effectiveNavigationAction,
    history.action,
    history.location.key,
    history.scopeId,
    requestedContinuation,
    registry,
    saveActiveWindow,
    saveNamedBinding,
  ]);

  React.useLayoutEffect(() => {
    const handleCanonicalContinuation = (event: Event) => {
      const request = (event as CustomEvent<unknown>).detail;
      if (!isCanonicalScrollContinuationRequest(request) || request.scopeId !== history.scopeId) return;
      canonicalContinuationRef.current = request;
    };
    const handleCanonicalContinuationCancel = (event: Event) => {
      if (canonicalContinuationRef.current === (event as CustomEvent<unknown>).detail) {
        canonicalContinuationRef.current = null;
      }
    };
    window.addEventListener(CANONICAL_SCROLL_CONTINUATION_EVENT, handleCanonicalContinuation);
    window.addEventListener(
      CANONICAL_SCROLL_CONTINUATION_CANCEL_EVENT,
      handleCanonicalContinuationCancel,
    );
    return () => {
      window.removeEventListener(CANONICAL_SCROLL_CONTINUATION_EVENT, handleCanonicalContinuation);
      window.removeEventListener(
        CANONICAL_SCROLL_CONTINUATION_CANCEL_EVENT,
        handleCanonicalContinuationCancel,
      );
    };
  }, [history.scopeId]);

  React.useEffect(() => {
    const handlePurge = () => {
      activationGenerationRef.current += 1;
      purgedRef.current = true;
      canonicalContinuationRef.current = null;
      cancelRestore();
      windowRestoreGuardRef.current = null;
      activeWindowRef.current = null;
      activeReadyOwnerRef.current = null;
      readyReportsRef.current.clear();
      for (const binding of namedBindingsRef.current.values()) {
        cancelNamedRestore(binding);
        binding.active = false;
      }
      namedBindingsRef.current.clear();
      previousNamedIdentityRef.current.clear();
      registry.purge();
    };
    window.addEventListener(NAVIGATION_PURGE_EVENT, handlePurge);
    return () => window.removeEventListener(NAVIGATION_PURGE_EVENT, handlePurge);
  }, [cancelNamedRestore, cancelRestore, registry]);

  React.useEffect(() => {
    const browserHistory = window.history as History & { scrollRestoration?: ScrollRestoration };
    if (!('scrollRestoration' in browserHistory)) return undefined;
    const previous = browserHistory.scrollRestoration;
    try {
      browserHistory.scrollRestoration = 'manual';
    } catch {
      return undefined;
    }
    return () => {
      try {
        if (browserHistory.scrollRestoration === 'manual' && previous) {
          browserHistory.scrollRestoration = previous;
        }
      } catch {
        // Browser ownership can disappear during document teardown.
      }
    };
  }, []);

  React.useEffect(() => {
    const handlePageHide = () => {
      saveActiveWindow();
      for (const binding of namedBindingsRef.current.values()) saveNamedBinding(binding);
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [saveActiveWindow, saveNamedBinding]);

  React.useLayoutEffect(() => () => {
    cancelRestore();
    for (const binding of namedBindingsRef.current.values()) {
      cancelNamedRestore(binding);
      saveNamedBinding(binding);
    }
    saveActiveWindow();
    activationGenerationRef.current += 1;
  }, [cancelNamedRestore, cancelRestore, saveActiveWindow, saveNamedBinding]);

  const isRestoring = React.useCallback((node: HTMLElement) => {
    if (node === document.scrollingElement) return windowRestoreGuardRef.current !== null;
    for (const binding of namedBindingsRef.current.values()) {
      if (binding.node === node && binding.active) return binding.restoreGuard || !binding.ready;
    }
    return false;
  }, []);
  const context = React.useMemo<NavigationScrollContextValue>(
    () => ({
      bindNamed,
      isRestoring,
      effectiveNavigationAction,
      reportNamedReady,
      reportReady,
    }),
    [bindNamed, isRestoring, effectiveNavigationAction, reportNamedReady, reportReady],
  );

  return (
    <NavigationScrollContext.Provider value={context}>
      {children}
    </NavigationScrollContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNavigationScrollReady({
  ready,
  viewKey,
}: {
  ready: boolean;
  viewKey: string;
}): void {
  const context = useRequiredScrollContext();
  const navigationHistory = useNavigationHistory();
  const ownerRef = React.useRef(Symbol('route-scroll-ready'));
  React.useLayoutEffect(() => context.reportReady(ownerRef.current, {
    scopeId: navigationHistory.scopeId,
    locationKey: navigationHistory.location.key,
    ready,
    viewKey,
  }), [context, navigationHistory.location.key, navigationHistory.scopeId, ready, viewKey]);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNavigationScrollRef<T extends HTMLElement>({
  key,
  ready,
  viewKey,
}: {
  key: string;
  ready: boolean;
  viewKey: string;
}): React.RefCallback<T> {
  const context = useRequiredScrollContext();
  const history = useNavigationHistory();
  const navigationActionRef = React.useRef(history.action);
  navigationActionRef.current = history.action;
  const ownerRef = React.useRef(Symbol('named-scroll'));
  const [node, setNode] = React.useState<T | null>(null);
  React.useLayoutEffect(() => {
    // Callback-ref state can lag one commit behind a conditional unmount. Do
    // not bind that detached node to the new history location, or its zeroed
    // coordinates can overwrite the destination's saved POP snapshot.
    if (!node || !node.isConnected) return undefined;
    return context.bindNamed(ownerRef.current, {
      scopeId: history.scopeId,
      locationKey: history.location.key,
      viewKey,
      key,
      node,
      navigationAction: navigationActionRef.current,
    });
  }, [context, history.location.key, history.scopeId, key, node, viewKey]);
  React.useLayoutEffect(() => {
    if (!node || !node.isConnected) return undefined;
    return context.reportNamedReady(ownerRef.current, {
      locationKey: history.location.key,
      action: context.effectiveNavigationAction,
      ready,
    });
  }, [context, history.location.key, node, ready]);
  return React.useCallback((next: T | null) => setNode(next), []);
}
